import { Base64Strings, usePictureUpload } from '~/common/utils/pictureUtils';
import { ButtonProps, IconButton, Tooltip, Spinner } from '@chakra-ui/react';
import { PictureType } from '~/common/entities/Pictures/PictureTypes';
import useTranslate from '~/common/utils/translate';
import { ImageOutlined } from '@material-ui/icons';
// @ts-expect-error This is a js only library
import ReactFileReader from 'react-file-reader';
import React, { useState } from 'react';

type PictureUploadButtonProps = ButtonProps & {
  pictureType: PictureType;
  projectOrJobId: string;
  ariaLabelText?: string;
  multipleFiles?: boolean;
  customHandleFileChange?: (files: Base64Strings) => Promise<void>;
  size?: object | 'lg' | 'md' | 'sm' | 'xs';
  customButton?: JSX.Element;
};

export const PictureUploadButton: React.FC<PictureUploadButtonProps> = ({
  pictureType,
  projectOrJobId,
  multipleFiles = true,
  customHandleFileChange = undefined,
  size = 'sm',
  customButton = undefined,
  ...props
}: PictureUploadButtonProps) => {
  const translate = useTranslate();
  const { handleFileChange } = usePictureUpload();
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (files: Base64Strings) => {
    if (isUploading) return;
    setIsUploading(true);
    try {
      if (customHandleFileChange) {
        await customHandleFileChange(files);
      } else {
        await handleFileChange(files, pictureType, projectOrJobId);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const buttonContent = isUploading ? <Spinner size="sm" /> : <ImageOutlined />;

  return (
    <ReactFileReader
      key={'ReactFileReader'}
      fileTypes={['.jpg']}
      base64={true}
      multipleFiles={multipleFiles}
      handleFiles={handleFileUpload}
    >
      {!customButton ? (
        <Tooltip
          label={translate('pictureUploadButton.uploadPicture')}
          aria-label={translate('pictureUploadButton.uploadPicture')}
        >
          <IconButton
            aria-label={
              translate('pictureUploadButton.uploadPicture') as string
            }
            icon={buttonContent}
            isRound
            size={size}
            isDisabled={isUploading}
            {...props}
          />
        </Tooltip>
      ) : (
        React.cloneElement(customButton, {
          disabled: isUploading,
          children: buttonContent,
        })
      )}
    </ReactFileReader>
  );
};
